<script lang="ts" setup>
	import { type ClassNameValue } from 'tailwind-merge'
	export type AtomBtnConfigClass = {
		btn: Partial<{
			default: string
			onHoverScale: string
			onHoverPseudo: string
			transition: string
			position: string
			base: string
			width?: string
			padding: string
		}>
		icon: Partial<{
			base: string
			opacity: string
			transition: string
		}>
		text: Partial<{
			font: string
			margin: string
			transition: string
			size: string
			base: string
		}>
	}

	const defaultConfig: AtomBtnConfigClass = {
		btn: {
			default: 'default-btn transition-2 justify-center',
			onHoverScale: '',
			onHoverPseudo: 'hover:before:bg-base-700',
			transition: 'transition-all  ease-out',
			position: 'relative',
			width: 'xs:min-w-[100px]',
			padding: 'xs:p-[18px_20px] p-[8px_20px] sm:p-[18px_46px]'
		},
		icon: {
			opacity: 'opacity-0 group-hover:opacity-100',
			transition: 'transitions ease-in-out'
		},
		text: {
			font: 'font-semibold',
			margin: '',
			transition: 'transition-2',
			size: 'text-xs xs:text-sm sm:text-base'
		}
	}
	const { ui } = defineProps<{
		hoverClass?: ClassNameValue
		ui?: Partial<AtomBtnConfigClass>
	}>()

	const extractValues = (arg: Object | undefined) => Object.values(arg ?? {})
</script>

<template>
	<button
		type="button"
		:class="
			cn(
				ui?.btn?.default ?? defaultConfig.btn.default,
				ui?.btn?.position ?? defaultConfig.btn.position,
				ui?.btn?.transition ?? defaultConfig.btn.transition,
				ui?.btn?.onHoverPseudo ?? defaultConfig.btn.onHoverPseudo,
				ui?.btn?.base ?? '',
				ui?.btn?.width ?? defaultConfig.btn?.width,
				ui?.btn?.padding ?? defaultConfig.btn?.padding,
				'group m-btn'
			)
		"
	>
		<div class="relative z-10 inline-flex items-center gap-x-2">
			<span
				class="flex-none z-10"
				:class="cn(extractValues(ui?.icon ?? defaultConfig.icon), ui?.icon?.base ?? '')"
			>
				<slot name="icon">
					<tools-icon icon="mcg-cap-sm" dir="internal-logo" width="28" height="16" />
				</slot>
			</span>

			<span
				:class="
					cn(
						'relative -translate-x-2.5 transition-transform ease-in-out group-hover:translate-x-0',
						hoverClass,
						ui?.text?.font ?? defaultConfig.text.font,
						ui?.text?.margin ?? defaultConfig.text.margin,
						ui?.text?.size ?? defaultConfig.text.size,
						ui?.text?.transition ?? defaultConfig.text.transition,
						ui?.text?.base ?? ''
					)
				"
			>
				<slot />
			</span>
		</div>
	</button>
</template>

<style>
	.m-btn {
		font-weight: 500;
		position: relative;
		border-radius: 200px;
		transition: all 500ms ease-in-out;
	}
	.m-btn::before {
		content: '';
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		height: 100%;
		width: 100%;
		transform: scale3d(0, 0, 0.5);
		background-color: transparent;
		border-radius: inherit;
		transform-style: preserve-3d;
		transform-origin: center;
		transition: all 300ms ease-in-out;
	}
	.m-btn:hover {
		font-weight: 700;
	}
	.m-btn:hover::before {
		transform: scale3d(1, 1.1, 0.5);
		z-index: 10;
	}

	@media screen and (min-width: 480px) {
		.m-btn:hover::before {
			transform: scale3d(1.1, 1.2, 0.5);
			z-index: 10;
		}
	}
</style>
